import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Tabs, Tab, Typography, Card, CardContent, Button, Chip, TextField } from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useAuth } from '../../contexts/AuthContext';
import ClinicalProfile from './pages/ClinicalProfile';
import TrialMatching from './pages/TrialMatching';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`patient-tabpanel-${index}`}
      aria-labelledby={`patient-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>{children}</Box>}
    </div>
  );
}

const PatientPortalContent = () => {
  const { user, jwtToken } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [savedTrials, setSavedTrials] = useState([]);
  const [matchingTrials, setMatchingTrials] = useState([]);
  const [patientStats, setPatientStats] = useState({});
  const [journeyQuestions, setJourneyQuestions] = useState({});

  useEffect(() => {
    if (user && jwtToken) {
      loadPatientData();
    }
  }, [user, jwtToken]);

  const loadPatientData = async () => {
    try {
      const response = await fetch(`/api/patient/${user.user_id}/dashboard`, {
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });

      if (!response.ok) {
        throw new Error(`Patient dashboard request failed: ${response.status}`);
      }

      const contentType = response.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        console.warn('Patient dashboard returned non-JSON response; skipping parse.');
        return;
      }

      const data = await response.json();
      
      setSavedTrials(data.savedTrials || []);
      setMatchingTrials(data.matchingTrials || []);
      setPatientStats(data.stats || {});
    } catch (error) {
      console.error('Error loading patient data:', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const removeFromSaved = async (trialId) => {
    try {
      await fetch(`/api/patient/${user.user_id}/saved-trials/${trialId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${jwtToken}` }
      });
      setSavedTrials(prev => prev.filter(trial => trial.id !== trialId));
    } catch (error) {
      console.error('Error removing trial:', error);
    }
  };

  const saveTrialFromMatches = async (trial) => {
    try {
      await fetch(`/api/patient/${user.user_id}/saved-trials`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${jwtToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(trial)
      });
      setSavedTrials(prev => [...prev, trial]);
    } catch (error) {
      console.error('Error saving trial:', error);
    }
  };

  const journeyPlans = matchingTrials.length > 0
    ? matchingTrials.slice(0, 3).map((trial, index) => ({
        id: trial.id || trial.nct_number || `match-${index}`,
        trialCode: trial.nct_number || `Trial-${index + 1}`,
        title: trial.brief_title || 'Clinical trial planning in progress',
        location: trial.location || 'Location to be confirmed',
        phase: trial.phase || 'Phase details pending',
        progress: Math.min(90, 40 + (index * 20)),
        nextStep: [
          'Finalize eligibility checklist with clinical trial management team',
          'Confirm sponsor intake requirements and consent packet',
          'Coordinate transportation and support services plan'
        ][index] || 'Complete readiness planning tasks'
      }))
    : [
        {
          id: 'journey-1',
          trialCode: 'NCT-PLANNING-101',
          title: 'Targeted Therapy Trial Readiness Plan',
          location: 'Primary Oncology Center',
          phase: 'Phase II',
          progress: 45,
          nextStep: 'Finalize eligibility checklist with clinical trial management team'
        },
        {
          id: 'journey-2',
          trialCode: 'NCT-PLANNING-205',
          title: 'Immunotherapy Combination Trial Planning',
          location: 'Regional Research Hospital',
          phase: 'Phase III',
          progress: 65,
          nextStep: 'Confirm sponsor intake requirements and consent packet'
        },
        {
          id: 'journey-3',
          trialCode: 'NCT-PLANNING-309',
          title: 'Precision Oncology Enrollment Preparation',
          location: 'Academic Trial Network',
          phase: 'Phase II/III',
          progress: 30,
          nextStep: 'Coordinate transportation and support services plan'
        }
      ];

  const handleJourneyQuestionChange = (trialId, value) => {
    setJourneyQuestions(prev => ({
      ...prev,
      [trialId]: value
    }));
  };

  const handleJourneyQuestionSubmit = (trialId) => {
    const question = journeyQuestions[trialId]?.trim();
    if (!question) {
      return;
    }

    setJourneyQuestions(prev => ({
      ...prev,
      [trialId]: ''
    }));
  };

  return (
    <Box sx={{ width: '100%', minWidth: 0, overflowX: 'hidden' }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={activeTab} 
          onChange={handleTabChange} 
          aria-label="patient portal tabs"
          variant="scrollable"
          scrollButtons="auto"
          sx={{ width: '100%', minWidth: 0 }}
        >
          <Tab label="📈 My Journey" />
          <Tab label="🔍 Trial Matching" />
          <Tab label={`💾 Saved Trials (${savedTrials.length})`} />
          <Tab label="📚 Resources & Support" />
          <Tab label="🧬 Clinical Profile" />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <Box>
          <Typography variant="h5" gutterBottom>
            My Trial Journey
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Track planning progress for each clinical trial you may participate in and send questions to the clinical trial management team, trial sponsor/pharma partner, or community support providers.
          </Typography>

          <Grid container spacing={3}>
            {journeyPlans.map((plan) => (
              <Grid item xs={12} key={plan.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                      <Box sx={{ flex: 1, minWidth: 260 }}>
                        <Typography variant="h6" gutterBottom>
                          {plan.trialCode}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          {plan.title}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          <Chip label={`📍 ${plan.location}`} size="small" />
                          <Chip label={`📅 ${plan.phase}`} size="small" />
                          <Chip label={`Progress: ${plan.progress}%`} color="primary" size="small" />
                        </Box>
                      </Box>
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Next planning step:</strong> {plan.nextStep}
                    </Typography>

                    <TextField
                      fullWidth
                      multiline
                      minRows={3}
                      label="Questions for trial team, sponsor/pharma, or community support partners"
                      placeholder="Type your question here..."
                      value={journeyQuestions[plan.id] || ''}
                      onChange={(event) => handleJourneyQuestionChange(plan.id, event.target.value)}
                    />

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                      <Button
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => navigate(`/trial-planning/${plan.id}`, { state: { trialPlan: plan } })}
                      >
                        Plan Trial
                      </Button>
                      <Button
                        variant="contained"
                        onClick={() => handleJourneyQuestionSubmit(plan.id)}
                        disabled={!(journeyQuestions[plan.id] || '').trim()}
                      >
                        Send Question
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={1}>
        <TrialMatching />
      </TabPanel>
      
      <TabPanel value={activeTab} index={2}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Your Saved Clinical Trials
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Trials you've bookmarked for further review and consideration.
          </Typography>

          {savedTrials.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>📋</Typography>
                <Typography variant="h6" gutterBottom>No Saved Trials Yet</Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  Start exploring trials in the matching section and save ones that interest you.
                </Typography>
                <Button 
                  variant="contained" 
                  onClick={() => setActiveTab(1)}
                  startIcon="🔍"
                >
                  Explore Trials
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {savedTrials.map((trial, index) => (
                <Grid item xs={12} key={index}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {trial.nct_number}
                          </Typography>
                          <Typography variant="body1" sx={{ mb: 2 }}>
                            {trial.brief_title}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            <Chip label={`📍 ${trial.location}`} size="small" />
                            <Chip label={`📅 ${trial.phase}`} size="small" />
                            <Chip label={`👥 ${trial.enrollment_count} enrolled`} size="small" />
                          </Box>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                          <Button size="small" variant="outlined">
                            View Details
                          </Button>
                          <Button 
                            size="small" 
                            color="error"
                            onClick={() => removeFromSaved(trial.id)}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={3}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Resources & Support
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Educational materials, support groups, and helpful resources for cancer patients and families.
          </Typography>

          <Grid container spacing={3}>
            {[
              { icon: '📖', title: 'Educational Materials', desc: 'Learn about clinical trials, treatment options, and what to expect, including readiness education series, nutrition support, and caregiver/family support programs.', action: 'Browse Resources & Programs' },
              { icon: '👥', title: 'Support Groups', desc: 'Connect with other patients and families going through similar experiences.', action: 'Find Groups' },
              { icon: '📞', title: 'Patient Navigation', desc: 'Get help navigating the healthcare system and finding resources.', action: 'Contact Navigator' },
              { icon: '💰', title: 'Financial Assistance', desc: 'Information about insurance, grants, and financial support programs.', action: 'Learn More' },
              { icon: '🚗', title: 'Transportation', desc: 'Help with getting to appointments and treatment centers.', action: 'Find Services' },
              { icon: '🏠', title: 'Housing', desc: 'Temporary housing options for patients traveling for treatment.', action: 'View Options' }
            ].map((resource, index) => (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent sx={{ flex: 1 }}>
                    <Typography variant="h3" sx={{ mb: 2 }}>{resource.icon}</Typography>
                    <Typography variant="h6" gutterBottom>{resource.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {resource.desc}
                    </Typography>
                    <Button variant="text" color="primary" size="small">
                      {resource.action} →
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      </TabPanel>
      
      <TabPanel value={activeTab} index={4}>
        <ClinicalProfile />
      </TabPanel>
    </Box>
  );
};

export default PatientPortalContent;